import axios from 'axios';
import { zip } from 'fflate';
import { mapWithLimit, S3_REQUEST_CONFIG } from '@/utils/mediaUpload';
import { uniqueNames } from '@/utils/zipNames';

// Downloading several files zips them in the browser, holding every file and
// the zip in memory at once. Selections past this ask the user to pick fewer.
export const MAX_ZIP_BYTES = 1024 * 1024 * 1024;
const FETCH_CONCURRENCY = 4;
// Already compressed: storing them as-is is as small and far faster.
const STORED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/dicom",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-m4v",
  "video/x-msvideo",
];

const DOWNLOAD_FAILED = "ดาวน์โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

// Presigned URLs carry their own credentials; the app's Authorization header
// must not go along, and an S3 403 must not trigger the permission redirect.
const fetchBlob = async (url, onBytes) => {
  const response = await axios({
    url,
    method: "GET",
    responseType: "blob",
    ...S3_REQUEST_CONFIG,
    onDownloadProgress: event => {
      if (onBytes) onBytes(event.loaded, event.total);
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    transformRequest: [
      (data, headers) => {
        delete headers["Authorization"];
        if (headers.common) delete headers.common["Authorization"];
        return data;
      },
    ],
  });
  return response.data;
};

const saveBlob = (blob, fileName) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(link);
};

const isExpired = error =>
  !!(error && error.response && error.response.status === 403);

// The page's presigned URL expires after 15 minutes; on a 403 ask once for a
// fresh one and try again.
const fetchFileBlob = async (file, refreshUrl, onBytes) => {
  try {
    return await fetchBlob(file.src, onBytes);
  } catch (error) {
    const freshUrl =
      isExpired(error) && refreshUrl ? await refreshUrl(file) : null;
    if (!freshUrl) throw error;
    return fetchBlob(freshUrl, onBytes);
  }
};

const zipAsync = entries =>
  new Promise((resolve, reject) => {
    zip(entries, (error, data) => (error ? reject(error) : resolve(data)));
  });

export function useFileOperations() {
  /**
   * Downloads one file under a readable name.
   *
   * @param {Object} item - formatted media file (src, fileType, fileSize…)
   * @param {Object} options
   * @param {string} options.fileName - see buildDownloadName
   * @param {(file: Object) => Promise<string|null>} [options.refreshUrl] - a
   *   fresh presigned URL when the page's one has expired
   * @param {(p: {done: number, total: number, percent: number}) => void} [options.onProgress]
   */
  const downloadFile = async (item, { fileName, refreshUrl, onProgress } = {}) => {
    const expected = Number(item.fileSize) || 0;
    const onBytes = (loaded, total) => {
      const size = total || expected;
      if (!onProgress || !size) return;
      onProgress({
        done: 0,
        total: 1,
        percent: Math.min(100, Math.round((loaded / size) * 100)),
      });
    };
    try {
      const blob = await fetchFileBlob(item, refreshUrl, onBytes);
      saveBlob(blob, fileName);
    } catch (error) {
      console.error("Error downloading file:", error);
      throw new Error(DOWNLOAD_FAILED);
    }
  };

  /**
   * Downloads several files as one zip, built in the browser.
   *
   * @param {Object[]} files - formatted media files (src, fileType, fileSize…)
   * @param {Object} options
   * @param {(file: Object) => string} options.fileNameOf - each file's name in the zip
   * @param {(file: Object) => Promise<string|null>} [options.refreshUrl] - a
   *   fresh presigned URL when the page's one has expired (15 minutes)
   * @param {(p: {done: number, total: number, percent: number, phase: string}) => void} [options.onProgress]
   *   phase is "fetch" while files arrive, then "zip" while the archive is built
   * @param {string} [options.zipName] - without extension
   * @returns {Promise<{ failed: string[] }>} names of files left out
   */
  const bulkDownload = async (
    files,
    { fileNameOf, refreshUrl, onProgress, zipName = "files" } = {}
  ) => {
    if (!files || files.length === 0) return { failed: [] };
    if (files.length === 1) {
      await downloadFile(files[0], {
        fileName: fileNameOf(files[0]),
        refreshUrl,
        onProgress,
      });
      return { failed: [] };
    }

    const totalBytes = files.reduce((sum, f) => sum + (Number(f.fileSize) || 0), 0);
    if (totalBytes > MAX_ZIP_BYTES) {
      throw new Error("ไฟล์ที่เลือกรวมกันเกิน 1 GB กรุณาเลือกไฟล์ให้น้อยลง");
    }

    const names = uniqueNames(files.map(fileNameOf));
    const blobs = new Array(files.length).fill(null);
    let done = 0;

    await mapWithLimit(
      files.map((_, i) => i),
      FETCH_CONCURRENCY,
      async i => {
        try {
          blobs[i] = await fetchFileBlob(files[i], refreshUrl);
        } catch (error) {
          console.error("Error downloading file:", error);
        } finally {
          done += 1;
          if (onProgress) {
            onProgress({
              done,
              total: files.length,
              percent: Math.round((done / files.length) * 100),
              phase: "fetch",
            });
          }
        }
      }
    );

    const entries = {};
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      if (!blobs[i]) {
        failed.push(names[i]);
        continue;
      }
      const level = STORED_TYPES.includes(files[i].fileType) ? 0 : 6;
      entries[names[i]] = [new Uint8Array(await blobs[i].arrayBuffer()), { level }];
    }
    if (Object.keys(entries).length === 0) {
      throw new Error(DOWNLOAD_FAILED);
    }
    if (onProgress) {
      onProgress({ done, total: files.length, percent: 100, phase: "zip" });
    }

    const zipped = await zipAsync(entries);
    saveBlob(new Blob([zipped], { type: "application/zip" }), `${zipName}.zip`);
    return { failed };
  };

  /**
   * Fetches files as File objects under their download names, for handing
   * to the phone's share sheet.
   *
   * @param {Object[]} files - formatted media files
   * @param {Object} options
   * @param {(file: Object) => string} options.fileNameOf
   * @param {(file: Object) => Promise<string|null>} [options.refreshUrl]
   * @param {(p: {done: number, total: number, percent: number, phase: string}) => void} [options.onProgress]
   * @returns {Promise<{ files: File[], failed: string[] }>}
   */
  const fetchFiles = async (files, { fileNameOf, refreshUrl, onProgress } = {}) => {
    const names = uniqueNames(files.map(fileNameOf));
    const fetched = new Array(files.length).fill(null);
    let done = 0;
    await mapWithLimit(
      files.map((_, i) => i),
      FETCH_CONCURRENCY,
      async i => {
        try {
          const blob = await fetchFileBlob(files[i], refreshUrl);
          fetched[i] = new File([blob], names[i], {
            type: files[i].fileType || blob.type,
          });
        } catch (error) {
          console.error("Error fetching file:", error);
        } finally {
          done += 1;
          if (onProgress) {
            onProgress({
              done,
              total: files.length,
              percent: Math.round((done / files.length) * 100),
              phase: "fetch",
            });
          }
        }
      }
    );
    return {
      files: fetched.filter(Boolean),
      failed: names.filter((_, i) => !fetched[i]),
    };
  };

  return {
    downloadFile,
    bulkDownload,
    fetchFiles
  };
}
